import React, { useRef, useState } from 'react'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import { has, isEmpty } from 'ramda'
import {
  Button,
  Loading,
  RadioButtonGroup,
  FlexRow,
  FlexCol,
} from 'components/patterns'
import ReactJson from 'react-json-view'
import { allowedLocationsRulesEngine } from 'rules/dash/allowedLocationsRules/engine'
import { RulesByModelCode } from 'rules/dash/allowedLocationsRules/rulesByModelCode'
import { makeRandId } from 'utils/identity'
import {
  UNASSIGNED_PANEL_PART,
  SWITCH_DETAIL_BY_MODEL_CODE,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import { fetchRulesUtilsData } from 'actions/rulesUtils'
import {
  getRulesUtilsData,
  getIsRulesUtilsLoading,
  getHasRulesUtilsError,
} from 'selectors/rulesUtils'

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.paragraphErrorColor};
  margin-top: 8px;
`

const createAllowedLocations = ({
  modelCode,
  components,
  layouts,
  databookCodes,
}) => {
  const assignedComponents = components.filter((comp) => comp.location)
  const unassignedComponents = components.filter((comp) => !comp.location)

  const locationsAsMap = layouts.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.name]: curr,
    }),
    {}
  )

  const componentsAsMap = assignedComponents.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.location]: curr,
    }),
    {}
  )

  const unassignedComponentsAsMap = unassignedComponents.reduce((acc, curr) => {
    const location = makeRandId(UNASSIGNED_PANEL_PART)
    return {
      ...acc,
      [location]: {
        ...curr,
        location,
        locationDetail: has('switchId', curr)
          ? SWITCH_DETAIL_BY_MODEL_CODE[modelCode]
          : curr.detail,
      },
    }
  }, {})

  const state = {
    dashModelCode: modelCode,
    locations: locationsAsMap,
    components: componentsAsMap,
    unassignedComponents: unassignedComponentsAsMap,
  }

  const allComponents = [
    ...Object.values(unassignedComponentsAsMap),
    ...Object.values(componentsAsMap),
  ]

  const comps = allComponents.reduce((compAcc, component) => {
    const locations = layouts.reduce(
      (layoustAcc, location) => {
        const result = allowedLocationsRulesEngine(state)(component)(location)

        if (result.allowed) {
          return {
            ...layoustAcc,
            allowed: [...layoustAcc.allowed, location.name],
          }
        }

        return {
          ...layoustAcc,
          disallowed: [
            ...layoustAcc.disallowed,
            {
              location: location.name,
              rule: result.rule,
            },
          ],
        }
      },
      {
        allowed: [],
        disallowed: [],
      }
    )

    return {
      ...compAcc,
      [component.panelNumber]: {
        ...component,
        ...locations,
      },
    }
  }, {})

  return comps
}

const processData = (data) => {
  const modelEntry = RulesByModelCode[data.dashboardType]
  if (!modelEntry) {
    throw new Error(
      `[RulesUtils] Unknown dashboardType "${data.dashboardType}" — not found in RulesByModelCode. ` +
        `Known types: ${Object.keys(RulesByModelCode).join(', ')}`
    )
  }
  const { databookCode, detail, type } = modelEntry

  const componentsWithAllowedLists = createAllowedLocations({
    ...data,
    modelCode: data.dashboardType,
  })

  const modelRules = {
    databookCode,
    detail,
    type,
  }

  return {
    componentsWithAllowedLists,
    modelRules,
    data,
  }
}

const RulesUtils = () => {
  const dispatch = useDispatch()
  const componentsRef = useRef(null)
  const layoutsRef = useRef(null)
  const [serialNumber, setSerialNumber] = useState('')
  const [selectedTab, setSelectedTab] = useState(0)
  const [simulateResult, setSimulateResult] = useState(null)

  const rulesUtilsData = useSelector(getRulesUtilsData)
  const isLoading = useSelector(getIsRulesUtilsLoading)
  const hasError = useSelector(getHasRulesUtilsError)

  // Process raw API data into the shape needed by the rules engine
  const data = rulesUtilsData ? processData(rulesUtilsData) : {}

  const handleFetchData = () => {
    dispatch(fetchRulesUtilsData(serialNumber))
  }

  const handleSimulateMovement = () => {
    const panelNumber = componentsRef.current.value
    const name = layoutsRef.current.value

    const { componentsWithAllowedLists } = data

    const allowed =
      componentsWithAllowedLists[panelNumber].allowed.includes(name)
    const disallowed = componentsWithAllowedLists[panelNumber].disallowed.find(
      (rule) => rule.location === name
    )

    const message = allowed ? (
      'Allowed to move.'
    ) : (
      <span>Not allowed to move because {disallowed.rule} rule.</span>
    )

    setSimulateResult(
      <span>
        <strong>Result: </strong>
        {message}
      </span>
    )
  }

  const tabs = [
    <FlexCol span={24} key="locations">
      <div style={{ marginTop: 20 }} />
      <ReactJson src={data.componentsWithAllowedLists} />
    </FlexCol>,

    <FlexCol span={24} key="rules">
      <div style={{ marginTop: 20 }}>
        <ReactJson src={data.modelRules} />
      </div>
    </FlexCol>,

    <FlexCol span={24} style={{ margin: 20 }} key="api">
      <div style={{ marginTop: 20 }}>
        <ReactJson src={data.data} />
      </div>
    </FlexCol>,
  ]

  const { components = [], layouts = [] } = data.data || {}

  return (
    <div style={{ padding: '10px 20px 10px' }}>
      <FlexRow>
        <FlexCol span={6}>
          <input
            disabled={isLoading}
            placeholder="Enter serial number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
          />
        </FlexCol>
        <FlexCol span={6}>
          <Button disabled={isLoading} onClick={handleFetchData}>
            Generate Rule Data
          </Button>
        </FlexCol>
      </FlexRow>
      <FlexRow>
        <FlexCol span={24}>
          {isLoading ? <Loading /> : <></>}
          {hasError && !isLoading && (
            <ErrorMessage>
              Failed to fetch rules data. The previous data has been cleared.
              Please try fetching again.
            </ErrorMessage>
          )}
        </FlexCol>
      </FlexRow>
      {!isEmpty(data) && (
        <div style={{ backgroundColor: '#efefef', padding: 4 }}>
          <FlexRow>
            <FlexCol span={6}>
              <div>
                <select
                  style={{ width: '100%', padding: 4 }}
                  ref={componentsRef}
                >
                  {components.map(({ panelNumber }) => (
                    <option key={panelNumber} value={panelNumber}>
                      {panelNumber}
                    </option>
                  ))}
                </select>
              </div>
            </FlexCol>
            <FlexCol span={6}>
              <div>
                <select style={{ width: '100%', padding: 4 }} ref={layoutsRef}>
                  {layouts.map(({ name }) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </FlexCol>
            <FlexCol span={6}>
              <div>
                <Button type="secondary" onClick={handleSimulateMovement}>
                  Simulate Movement
                </Button>
              </div>
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol span={24}>
              <div>{simulateResult}</div>
            </FlexCol>
          </FlexRow>
        </div>
      )}
      <FlexRow>
        <FlexCol span={24}>
          <div style={{ padding: 4 }}>
            {!isEmpty(data) && (
              <RadioButtonGroup
                style={{ marginTop: 20 }}
                currentValue={selectedTab}
                buttons={[
                  {
                    value: 0,
                    text: `Allowed locations by panelNumber`,
                  },
                  {
                    value: 1,
                    text: `rules for model code ${data.data.dashboardType}`,
                  },
                  {
                    value: 2,
                    text: `Dashboard API for ${serialNumber}`,
                  },
                ]}
                onChange={({ target }) => {
                  setSelectedTab(target.value)
                }}
              />
            )}
          </div>
        </FlexCol>
      </FlexRow>
      <FlexRow>
        <div>{!isEmpty(data) && tabs[selectedTab]}</div>
      </FlexRow>
    </div>
  )
}

export default RulesUtils
